import type { ProposalEntry } from '../types/types.TimesheetProposals';
import { rangesOverlap, type TimeRange, toMs } from './util.timesheetTransformers';

// Comparisons run in epoch ms, never on the ISO strings: slot timestamps come
// from the day-local generator ("…+02:00") while entry/activity timestamps
// come from the server in UTC ("…Z"). A lexical compare would treat those as
// different instants even when they're identical. `toMs` and `rangesOverlap`
// are the shared primitives in util.timesheetTransformers.

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ClaimSpec = {
  range: TimeRange;
  /** null = "release" — evict overlapping entries, don't create a new one. */
  projectId: number | null;
  labelIds: number[];
  resourceId: string | null;
  date: string;
  note?: string | null;
};

export type ClaimEffect =
  | {
      kind: 'patchRange';
      entryId: string;
      started_at: string;
      ended_at: string;
    }
  | {
      kind: 'createEntry';
      date: string;
      timely_project_id: number;
      timely_label_ids: number[];
      resource_id: string | null;
      note: string | null;
      started_at: string;
      ended_at: string;
    }
  | {
      kind: 'delete';
      entryId: string;
    };

export type ClaimResult = {
  /** Effects in apply-order. */
  effects: ClaimEffect[];
  /** Index into `effects` of the target entry effect; -1 when no target. */
  targetEffectIndex: number;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type RangedEntry = ProposalEntry & TimeRange;

/** True iff the entry has a real (non-null, non-empty) range. */
function hasRange(e: ProposalEntry): e is RangedEntry {
  return Boolean(e.started_at && e.ended_at);
}

/** Sort a label array ascending and return a new array. */
function sortedLabels(ids: number[] | null | undefined): number[] {
  return [...(ids ?? [])].sort((a, b) => a - b);
}

/**
 * Two entries (or an entry and a claim) share the same identity iff project,
 * sorted labels, and resourceId all match.
 */
function sameIdentity(
  aProject: number | null,
  aLabels: number[] | null | undefined,
  aResource: string | null | undefined,
  bProject: number | null,
  bLabels: number[] | null | undefined,
  bResource: string | null | undefined
): boolean {
  if (aProject !== bProject) return false;
  const aSorted = sortedLabels(aLabels);
  const bSorted = sortedLabels(bLabels);
  if (aSorted.length !== bSorted.length) return false;
  for (let i = 0; i < aSorted.length; i++) {
    if (aSorted[i] !== bSorted[i]) return false;
  }
  return (aResource ?? null) === (bResource ?? null);
}

/** Strict overlap wrapper — delegates to `rangesOverlap` to avoid duplication. */
function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return rangesOverlap(
    { started_at: aStart, ended_at: aEnd },
    { started_at: bStart, ended_at: bEnd }
  );
}

/** Adjacent: one range ends exactly where the other starts (same instant). */
function adjacent(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return toMs(aEnd) === toMs(bStart) || toMs(bEnd) === toMs(aStart);
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Computes the set of mutations needed for a "claim" gesture:
 * the caller wants [claim.range] to be owned by the identity
 * described in `claim`. Returns ordered effects and the index
 * of the effect that ends up as the "target entry".
 *
 * Ambiguity resolution: when a claim is exactly identical to an existing
 * same-identity entry's range, we still emit a no-op patchRange — the
 * executor can skip it cheaply, and keeping the code path uniform is simpler.
 */
export function computeClaimEffects(claim: ClaimSpec, entries: ProposalEntry[]): ClaimResult {
  const { range: claimRange, projectId: claimProject } = claim;

  // A zero-length claim is a no-op.
  if (toMs(claimRange.started_at) === toMs(claimRange.ended_at)) {
    return { effects: [], targetEffectIndex: -1 };
  }

  const claimStartMs = toMs(claimRange.started_at);
  const claimEndMs = toMs(claimRange.ended_at);

  const effects: ClaimEffect[] = [];
  let targetEffectIndex = -1;

  // Separate ranged entries from null-range entries (null-range ignored).
  const ranged = entries.filter(hasRange);

  // Partition into same-identity (merge candidates) and different-identity.
  const mergeSet: RangedEntry[] = [];
  const evictSet: RangedEntry[] = [];

  for (const e of ranged) {
    const isMatch = sameIdentity(
      claimProject,
      claim.labelIds,
      claim.resourceId,
      // timely_project_id is optional on the Rails entry shape (number | null |
      // undefined); sameIdentity compares against number | null.
      e.timely_project_id ?? null,
      e.timely_label_ids,
      e.resource_id ?? null
    );

    if (isMatch) {
      // Merge if overlapping OR adjacent.
      if (
        overlaps(e.started_at, e.ended_at, claimRange.started_at, claimRange.ended_at) ||
        adjacent(e.started_at, e.ended_at, claimRange.started_at, claimRange.ended_at)
      ) {
        mergeSet.push(e);
      }
      // Non-adjacent same-identity entries are left alone (no merge across gaps).
    } else {
      // Different identity: only evict if they actually overlap (not just touch).
      if (overlaps(e.started_at, e.ended_at, claimRange.started_at, claimRange.ended_at)) {
        evictSet.push(e);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Step 1: Evict different-identity overlapping entries.
  // -------------------------------------------------------------------------
  for (const e of evictSet) {
    const eStartMs = toMs(e.started_at);
    const eEndMs = toMs(e.ended_at);
    const claimCoversLeft = claimStartMs <= eStartMs && claimEndMs < eEndMs;
    const claimCoversRight = claimStartMs > eStartMs && claimEndMs >= eEndMs;
    const claimFullyCovers = claimStartMs <= eStartMs && claimEndMs >= eEndMs;
    const claimInsideEntry = claimStartMs > eStartMs && claimEndMs < eEndMs;

    if (claimFullyCovers && !claimInsideEntry) {
      // Claim covers entry entirely → delete.
      effects.push({ kind: 'delete', entryId: e.id });
    } else if (claimInsideEntry) {
      // Split: claim is fully inside entry.
      // PATCH original to [e.start, claim.start].
      effects.push({
        kind: 'patchRange',
        entryId: e.id,
        started_at: e.started_at,
        ended_at: claimRange.started_at,
      });
      // CREATE right fragment with original's identity.
      effects.push({
        kind: 'createEntry',
        date: e.date,
        timely_project_id: e.timely_project_id as number,
        timely_label_ids: e.timely_label_ids ?? [],
        resource_id: e.resource_id ?? null,
        note: e.note ?? null,
        started_at: claimRange.ended_at,
        ended_at: e.ended_at,
      });
    } else if (claimCoversLeft) {
      // Claim eats the left edge → PATCH entry to start at claim end.
      effects.push({
        kind: 'patchRange',
        entryId: e.id,
        started_at: claimRange.ended_at,
        ended_at: e.ended_at,
      });
    } else if (claimCoversRight) {
      // Claim eats the right edge → PATCH entry to end at claim start.
      effects.push({
        kind: 'patchRange',
        entryId: e.id,
        started_at: e.started_at,
        ended_at: claimRange.started_at,
      });
    }
  }

  // -------------------------------------------------------------------------
  // Step 2: Handle same-identity merge set.
  // -------------------------------------------------------------------------
  if (mergeSet.length > 0) {
    // Union of all merge-set ranges + claim range (compared in epoch ms, so
    // the chosen endpoints reflect the real instants regardless of input
    // format).
    let unionStart = claimRange.started_at;
    let unionStartMs = claimStartMs;
    let unionEnd = claimRange.ended_at;
    let unionEndMs = claimEndMs;
    for (const e of mergeSet) {
      const eStartMs = toMs(e.started_at);
      const eEndMs = toMs(e.ended_at);
      if (eStartMs < unionStartMs) {
        unionStart = e.started_at;
        unionStartMs = eStartMs;
      }
      if (eEndMs > unionEndMs) {
        unionEnd = e.ended_at;
        unionEndMs = eEndMs;
      }
    }

    // Pick stable target: the entry with the lexically lowest id.
    const sorted = [...mergeSet].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const target = sorted[0];
    const rest = sorted.slice(1);

    const patchEffect: ClaimEffect = {
      kind: 'patchRange',
      entryId: target.id,
      started_at: unionStart,
      ended_at: unionEnd,
    };
    targetEffectIndex = effects.length;
    effects.push(patchEffect);

    for (const e of rest) {
      effects.push({ kind: 'delete', entryId: e.id });
    }
  } else if (claimProject !== null) {
    // No same-identity merge partner found — CREATE a new entry for the claim.
    const createEffect: ClaimEffect = {
      kind: 'createEntry',
      date: claim.date,
      timely_project_id: claimProject,
      timely_label_ids: sortedLabels(claim.labelIds),
      resource_id: claim.resourceId,
      note: claim.note ?? null,
      started_at: claimRange.started_at,
      ended_at: claimRange.ended_at,
    };
    targetEffectIndex = effects.length;
    effects.push(createEffect);
  }
  // else: release claim (projectId null) with no merge set → no target, no create.

  return { effects, targetEffectIndex };
}
